import React, { useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { 
  ShoppingCart, 
  Plus, 
  Minus, 
  Trash2, 
  CreditCard, 
  Wallet,
  Home,
  Store,
  Bike,
  MapPin,
  ArrowLeft,
  CheckCircle,
  Search,
  X,
  Eye,
  EyeOff,
  Receipt,
  User,
  Phone,
  MessageSquare,
  Loader2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  TrendingUp,
  Scale,
  Edit3
} from 'lucide-react';
import { toast } from 'sonner';

import { getBackendUrl } from './utils/api';
import PDVAddressModal from './components/pdv/PDVAddressModal';
import PDVWeightModal from './components/pdv/PDVWeightModal';
import PDVAdditionalsModal from './components/pdv/PDVAdditionalsModal';
import PDVProductsPanel from './components/pdv/PDVProductsPanel';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const PDVScreen = ({ onClose, editingOrder = null }) => {
  const isEditMode = !!editingOrder;
  // Estados principais
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showProductImages, setShowProductImages] = useState(false);
  const [addingToCart, setAddingToCart] = useState(null);
  
  // Estados de checkout
  const [step, setStep] = useState('menu'); // menu, checkout
  const [deliveryType, setDeliveryType] = useState('balcao');
  const [paymentMethod, setPaymentMethod] = useState('dinheiro');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [tableNumber, setTableNumber] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [isFinalizingOrder, setIsFinalizingOrder] = useState(false);
  const [showMiniCart, setShowMiniCart] = useState(true);
  const [paidAmount, setPaidAmount] = useState('');
  const [notifyCustomer, setNotifyCustomer] = useState(isEditMode);
  const [address, setAddress] = useState({
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    zipcode: ''
  });
  
  // Modal states
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [loadingCep, setLoadingCep] = useState(false);
  
  // Delivery zones (bairros e taxas)
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [selectedDeliveryZone, setSelectedDeliveryZone] = useState(null);
  const [loadingZones, setLoadingZones] = useState(false);
  
  // ========== ESTADOS DO MODAL DE ADICIONAIS ==========
  const [additionalGroups, setAdditionalGroups] = useState([]);
  const [showAdditionalsModal, setShowAdditionalsModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedAdditionals, setSelectedAdditionals] = useState({});
  const [productQuantity, setProductQuantity] = useState(1);
  const [loadingAdditionals, setLoadingAdditionals] = useState(false);
  const [productGroupsWithItems, setProductGroupsWithItems] = useState([]); // Grupos com items carregados

  // ========== ESTADOS DO MODAL DE VENDA POR PESO ==========
  const [showWeightModal, setShowWeightModal] = useState(false);
  const [weightItems, setWeightItems] = useState([]);
  const [selectedWeightItem, setSelectedWeightItem] = useState(null);
  const [weightInGrams, setWeightInGrams] = useState('');
  const [loadingWeightItems, setLoadingWeightItems] = useState(false);
  const [showNewWeightItemForm, setShowNewWeightItemForm] = useState(false);
  const [newWeightItemName, setNewWeightItemName] = useState('');
  const [newWeightItemPrice, setNewWeightItemPrice] = useState('');
  const [editingWeightItem, setEditingWeightItem] = useState(null);

  // Buscar bairros cadastrados
  const fetchDeliveryZones = async () => {
    setLoadingZones(true);
    try {
      const response = await axios.get(`${API}/delivery-zones`);
      setDeliveryZones(response.data || []);
    } catch (error) {
      console.error('Error fetching delivery zones:', error);
      setDeliveryZones([]);
    }
    setLoadingZones(false);
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchDeliveryZones();
    fetchAdditionalGroups();
    fetchWeightItems();
    
    // Bloqueia scroll do body quando PDV abre
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.width = '100%';
    document.body.style.height = '100%';
    
    // Restaura scroll quando PDV fecha
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data.filter(p => p.available));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Erro ao carregar produtos');
      setLoading(false);
    }
  };

  // Modo edição: pré-preenche carrinho e formulário do pedido sendo editado
  useEffect(() => {
    if (!isEditMode || !editingOrder || loading) return;
    const cartFromOrder = (editingOrder.items || []).map((it, idx) => {
      const prod = products.find(p => p.id === (it.product_id || it.id));
      return {
        id: it.product_id || it.id || `legacy-${idx}`,
        cartId: `edit-${idx}-${Date.now()}`,
        name: it.product_name || it.name || (prod?.name) || 'Item',
        price: parseFloat(it.price || prod?.price || 0),
        quantity: parseFloat(it.quantity || 1),
        image: prod?.image,
        category_id: prod?.category_id,
        additionals: (it.additionals || []).map(a => ({
          id: a.id || a.additional_id,
          name: a.name || a.additional_name,
          price: parseFloat(a.price || 0),
          group_id: a.group_id || '',
          group_name: a.group_name || ''
        }))
      };
    });
    setCart(cartFromOrder);
    setCustomerName(editingOrder.customer_name || '');
    setCustomerPhone(editingOrder.customer_phone || '');
    setTableNumber(editingOrder.table_number || '');
    setOrderNotes(editingOrder.notes || '');
    if (editingOrder.delivery_type) setDeliveryType(editingOrder.delivery_type);
    if (editingOrder.payment_method) setPaymentMethod(editingOrder.payment_method);
    if (editingOrder.address) {
      setAddress({
        street: editingOrder.address.street || '',
        number: editingOrder.address.number || '',
        complement: editingOrder.address.complement || '',
        neighborhood: editingOrder.address.neighborhood || '',
        city: editingOrder.address.city || '',
        state: editingOrder.address.state || '',
        zipcode: editingOrder.address.zipcode || ''
      });
    }
    const zoneName = editingOrder.delivery_zone_name || editingOrder.address?.neighborhood;
    if (zoneName && Array.isArray(deliveryZones) && deliveryZones.length > 0) {
      const matched = deliveryZones.find(
        (z) => (z.name || '').trim().toLowerCase() === String(zoneName).trim().toLowerCase()
      );
      if (matched) setSelectedDeliveryZone(matched);
    }
    if (editingOrder.change_for != null) {
      setPaidAmount(String(editingOrder.change_for));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editingOrder, loading, products, deliveryZones]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.filter(c => c.active));
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  // Buscar grupos de adicionais
  const fetchAdditionalGroups = async () => {
    try {
      const response = await axios.get(`${API}/additional-groups`);
      setAdditionalGroups(response.data || []);
    } catch (error) {
      console.error('Error fetching additional groups:', error);
    }
  };

  // ========== FUNÇÕES DE VENDA POR PESO ==========
  const fetchWeightItems = async () => {
    setLoadingWeightItems(true);
    try {
      const response = await axios.get(`${API}/weight-items`);
      setWeightItems(response.data || []);
    } catch (error) {
      console.error('Error fetching weight items:', error);
      setWeightItems([]);
    }
    setLoadingWeightItems(false);
  };

  const createWeightItem = async () => {
    if (!newWeightItemName.trim() || !newWeightItemPrice || parseFloat(newWeightItemPrice) <= 0) {
      toast.error('Preencha nome e preço válido');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/weight-items`, {
        name: newWeightItemName.trim(),
        price_per_kg: parseFloat(newWeightItemPrice)
      });
      
      setWeightItems([...weightItems, response.data]);
      setNewWeightItemName('');
      setNewWeightItemPrice('');
      setShowNewWeightItemForm(false);
      toast.success('Item criado com sucesso!');
    } catch (error) {
      console.error('Error creating weight item:', error);
      toast.error('Erro ao criar item');
    }
  };

  const updateWeightItem = async () => {
    if (!editingWeightItem) return;
    
    if (!newWeightItemName.trim() || !newWeightItemPrice || parseFloat(newWeightItemPrice) <= 0) {
      toast.error('Preencha nome e preço válido');
      return;
    }
    
    try {
      const response = await axios.put(`${API}/weight-items/${editingWeightItem.id}`, {
        name: newWeightItemName.trim(),
        price_per_kg: parseFloat(newWeightItemPrice)
      });
      
      setWeightItems(weightItems.map(item => 
        item.id === editingWeightItem.id ? response.data : item
      ));
      setEditingWeightItem(null);
      setNewWeightItemName('');
      setNewWeightItemPrice('');
      setShowNewWeightItemForm(false);
      toast.success('Item atualizado!');
    } catch (error) {
      console.error('Error updating weight item:', error);
      toast.error('Erro ao atualizar item');
    }
  };

  const deleteWeightItem = async (itemId) => {
    if (!window.confirm('Remover este item?')) return;
    
    try {
      await axios.delete(`${API}/weight-items/${itemId}`);
      setWeightItems(weightItems.filter(item => item.id !== itemId));
      
      if (selectedWeightItem?.id === itemId) {
        setSelectedWeightItem(null);
        setWeightInGrams('');
      }
      toast.success('Item removido!');
    } catch (error) {
      console.error('Error deleting weight item:', error);
      toast.error('Erro ao remover item');
    }
  };

  // Calcular total do item por peso
  const calculateWeightTotal = useMemo(() => {
    if (!selectedWeightItem || !weightInGrams || parseFloat(weightInGrams) <= 0) return 0;
    const grams = parseFloat(weightInGrams);
    return (grams / 1000) * selectedWeightItem.price_per_kg;
  }, [selectedWeightItem, weightInGrams]);

  // Adicionar item por peso ao carrinho
  const addWeightItemToCart = () => {
    if (!selectedWeightItem || !weightInGrams || calculateWeightTotal <= 0) return;
    
    const grams = parseFloat(weightInGrams);
    const weightLabel = grams >= 1000 ? `${(grams / 1000).toFixed(2).replace('.', ',')}kg` : `${grams}g`;
    
    const cartItem = {
      id: `weight-${selectedWeightItem.id}-${Date.now()}`,
      name: `${selectedWeightItem.name} (${weightLabel})`,
      price: calculateWeightTotal,
      quantity: 1,
      isWeightItem: true,
      weightItemId: selectedWeightItem.id,
      weightInGrams: grams,
      additionals: []
    };
    
    setCart([...cart, cartItem]);
    toast.success('Adicionado ao carrinho! 🎉', {
      description: `${cartItem.name} - R$ ${calculateWeightTotal.toFixed(2)}`,
      duration: 2000
    });
    
    // Reset modal state
    setSelectedWeightItem(null);
    setWeightInGrams('');
    setShowWeightModal(false);
  };

  // Abrir modal de peso
  const openWeightModal = () => {
    setSelectedWeightItem(null);
    setWeightInGrams('');
    setShowNewWeightItemForm(false);
    setEditingWeightItem(null);
    setNewWeightItemName('');
    setNewWeightItemPrice('');
    setShowWeightModal(true);
  };

  // Iniciar edição de item
  const startEditWeightItem = (item) => {
    setEditingWeightItem(item);
    setNewWeightItemName(item.name);
    setNewWeightItemPrice(item.price_per_kg.toString());
    setShowNewWeightItemForm(true);
  };

  // Buscar CEP com debounce
  const searchCep = async (cep) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) return;

    setLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cleanCep}/json/`);
      const data = await response.json();
      
      if (!data.erro) {
        setAddress(prev => ({
          ...prev,
          street: data.logradouro || '',
          neighborhood: data.bairro || '',
          city: data.localidade || '',
          state: data.uf || '',
          zipcode: cep
        }));
        toast.success('CEP encontrado!');
      } else {
        toast.error('CEP não encontrado');
      }
    } catch (error) {
      toast.error('Erro ao buscar CEP');
    } finally {
      setLoadingCep(false);
    }
  };

  // Funções do carrinho com animação
  // ========== FUNÇÃO PARA OBTER GRUPOS DE ADICIONAIS DO PRODUTO ==========
  const getProductAdditionalGroups = useCallback((product) => {
    // Verificar se o produto tem additional_groups configurados
    const productGroupIds = product.additional_groups || [];
    if (productGroupIds.length === 0) return [];
    
    // Filtrar os grupos de adicionais que pertencem a este produto
    return additionalGroups.filter(group => 
      productGroupIds.includes(group.id) && group.active !== false
    );
  }, [additionalGroups]);

  // ========== ADICIONAR AO CARRINHO DIRETO (SEM ADICIONAIS) ==========
  const addToCartDirect = useCallback((product) => {
    setAddingToCart(product.id);
    
    const existingItem = cart.find(item => 
      item.id === product.id && (!item.additionals || item.additionals.length === 0)
    );
    
    if (existingItem) {
      setCart(cart.map(item => 
        item.id === product.id && (!item.additionals || item.additionals.length === 0)
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1, additionals: [] }]);
    }
    
    toast.success('Adicionado ao carrinho! 🎉', {
      description: `${product.name} - R$ ${product.price.toFixed(2)}`,
      duration: 2000
    });

    setTimeout(() => setAddingToCart(null), 500);
  }, [cart]);

  // ========== CARREGAR ADICIONAIS DE CADA GRUPO ==========
  const loadGroupAdditionals = useCallback(async (groupIds) => {
    setLoadingAdditionals(true);
    try {
      // Carregar adicionais de todos os grupos em paralelo
      const groupsPromises = groupIds.map(async (groupId) => {
        try {
          const response = await axios.get(`${API}/additional-groups/${groupId}/additionals`);
          const group = additionalGroups.find(g => g.id === groupId);
          return {
            ...group,
            id: groupId,
            name: response.data.group_name || group?.name || 'Grupo',
            additionals: response.data.additionals || [],
            min_items: group?.min_choices || 0,
            max_items: group?.max_choices || 10,
            is_required: group?.is_required || false
          };
        } catch (error) {
          console.error(`Error loading additionals for group ${groupId}:`, error);
          return null;
        }
      });
      
      const loadedGroups = await Promise.all(groupsPromises);
      const validGroups = loadedGroups.filter(g => g !== null && g.additionals && g.additionals.length > 0);
      setProductGroupsWithItems(validGroups);
    } catch (error) {
      console.error('Error loading group additionals:', error);
      setProductGroupsWithItems([]);
    } finally {
      setLoadingAdditionals(false);
    }
  }, [additionalGroups]);

  // ========== ABRIR MODAL DE ADICIONAIS OU ADICIONAR DIRETO ==========
  const handleProductClick = useCallback(async (product) => {
    const productGroupIds = product.additional_groups || [];
    
    if (productGroupIds.length > 0) {
      // Tem adicionais - abrir modal e carregar itens
      setSelectedProduct(product);
      setSelectedAdditionals({});
      setProductQuantity(1);
      setProductGroupsWithItems([]);
      setShowAdditionalsModal(true);
      
      // Carregar adicionais dos grupos
      await loadGroupAdditionals(productGroupIds);
    } else {
      // Não tem adicionais - adicionar direto
      addToCartDirect(product);
    }
  }, [loadGroupAdditionals, addToCartDirect]);

  // ========== TOGGLE DE ADICIONAL NO MODAL ==========
  const toggleAdditional = useCallback((groupId, additional) => {
    setSelectedAdditionals(prev => {
      const groupAdditionals = prev[groupId] || [];
      const group = productGroupsWithItems.find(g => g.id === groupId);
      const maxItems = group?.max_items || 10;
      
      const isSelected = groupAdditionals.some(a => a.id === additional.id);
      
      if (isSelected) {
        // Remove o adicional
        return {
          ...prev,
          [groupId]: groupAdditionals.filter(a => a.id !== additional.id)
        };
      } else {
        // Adiciona o adicional (respeitando o máximo)
        if (groupAdditionals.length >= maxItems) {
          toast.error(`Máximo de ${maxItems} itens neste grupo`);
          return prev;
        }
        return {
          ...prev,
          [groupId]: [...groupAdditionals, additional]
        };
      }
    });
  }, [productGroupsWithItems]);

  // ========== VERIFICAR SE PODE ADICIONAR (GRUPOS OBRIGATÓRIOS) ==========
  const canAddToCart = useMemo(() => {
    if (!selectedProduct) return false;
    if (loadingAdditionals) return false;
    
    // Se não tem grupos com itens, pode adicionar
    if (productGroupsWithItems.length === 0) return true;
    
    for (const group of productGroupsWithItems) {
      const minItems = group.min_items || 0;
      const selectedCount = (selectedAdditionals[group.id] || []).length;
      
      if (minItems > 0 && selectedCount < minItems) {
        return false;
      }
    }
    return true;
  }, [selectedProduct, selectedAdditionals, productGroupsWithItems, loadingAdditionals]);

  // ========== CALCULAR TOTAL DO PRODUTO COM ADICIONAIS ==========
  const calculateProductTotal = useMemo(() => {
    if (!selectedProduct) return 0;
    
    let total = selectedProduct.price * productQuantity;
    
    Object.values(selectedAdditionals).forEach(additionals => {
      additionals.forEach(add => {
        total += (add.price || 0) * productQuantity;
      });
    });
    
    return total;
  }, [selectedProduct, selectedAdditionals, productQuantity]);

  // ========== ADICIONAR AO CARRINHO COM ADICIONAIS ==========
  const addToCartWithAdditionals = useCallback(() => {
    if (!selectedProduct || !canAddToCart) return;
    
    // Montar lista de adicionais selecionados
    const allAdditionals = [];
    Object.entries(selectedAdditionals).forEach(([groupId, additionals]) => {
      const group = productGroupsWithItems.find(g => g.id === groupId);
      additionals.forEach(add => {
        allAdditionals.push({
          ...add,
          group_name: group?.name || '',
          group_id: groupId
        });
      });
    });
    
    // Calcular preço com adicionais
    const additionalsTotal = allAdditionals.reduce((sum, a) => sum + (a.price || 0), 0);
    const totalPrice = selectedProduct.price + additionalsTotal;
    
    // Criar item único do carrinho (com ID único para permitir mesmo produto com adicionais diferentes)
    const cartItemId = `${selectedProduct.id}_${Date.now()}`;
    
    const cartItem = {
      ...selectedProduct,
      cartItemId,
      quantity: productQuantity,
      additionals: allAdditionals,
      totalPrice
    };
    
    setCart(prev => [...prev, cartItem]);
    
    // Feedback
    const additionalsText = allAdditionals.length > 0 
      ? ` + ${allAdditionals.length} adicional(is)` 
      : '';
    toast.success('Adicionado ao carrinho! 🎉', {
      description: `${selectedProduct.name}${additionalsText}`,
      duration: 2000
    });
    
    // Fechar modal e resetar estados
    setShowAdditionalsModal(false);
    setSelectedProduct(null);
    setSelectedAdditionals({});
    setProductQuantity(1);
    setProductGroupsWithItems([]);
  }, [selectedProduct, selectedAdditionals, productQuantity, canAddToCart, productGroupsWithItems]);

  // ========== FUNÇÃO LEGADA (para compatibilidade) ==========
  const addToCart = useCallback((product) => {
    handleProductClick(product);
  }, [handleProductClick]);

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(cart.map(item => 
      (item.cartItemId || item.id) === productId 
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => (item.cartItemId || item.id) !== productId));
    toast.info('Produto removido', { duration: 1500 });
  };

  const clearCart = () => {
    setCart([]);
    toast.info('Carrinho limpo');
  };

  // Cálculos memoizados
  const { subtotal, deliveryFee, total, itemCount } = useMemo(() => {
    const subtotal = cart.reduce((sum, item) => {
      const itemPrice = item.totalPrice || item.price;
      return sum + (itemPrice * item.quantity);
    }, 0);
    const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
    const deliveryFee = (deliveryType === 'delivery' && selectedDeliveryZone) 
      ? selectedDeliveryZone.fee 
      : 0;
    return { subtotal, deliveryFee, total: subtotal + deliveryFee, itemCount };
  }, [cart, deliveryType, selectedDeliveryZone]);

  // Calcular troco
  const changeAmount = useMemo(() => {
    if (paymentMethod === 'dinheiro' && paidAmount) {
      const paid = parseFloat(paidAmount);
      if (!isNaN(paid) && paid >= total) {
        return paid - total;
      }
    }
    return 0;
  }, [paymentMethod, paidAmount, total]);

  // Filtrar produtos com debounce
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesCategory = selectedCategory === 'all' || product.category_id === selectedCategory;
      const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            product.description?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [products, selectedCategory, searchTerm]);

  // Categorias com contagem
  const categoriesWithCount = useMemo(() => {
    return categories.map(cat => ({
      ...cat,
      count: products.filter(p => p.category_id === cat.id).length
    }));
  }, [categories, products]);

  // Finalizar pedido
  const finalizarPedido = async () => {
    if (cart.length === 0) {
      toast.error('Adicione produtos ao carrinho');
      return;
    }

    if (!customerName.trim()) {
      toast.error('Informe o nome do cliente');
      return;
    }

    if (deliveryType === 'delivery' && (!address.street || !address.number || !selectedDeliveryZone)) {
      toast.error('Preencha o endereço de entrega completo');
      setShowAddressModal(true);
      return;
    }

    if (deliveryType === 'mesa' && !tableNumber) {
      toast.error('Informe o número da mesa');
      return;
    }

    setIsFinalizingOrder(true);

    try {
      const orderNumber = `${Date.now().toString().slice(-6)}`;
      
      const orderData = {
        order_number: orderNumber,
        customer_name: customerName,
        customer_phone: customerPhone,
        table_number: deliveryType === 'mesa' ? tableNumber : null,
        delivery_type: deliveryType,
        payment_method: paymentMethod,
        // Troco: enviar somente se for dinheiro e o valor pago for >= total
        change_for: (paymentMethod === 'dinheiro' && paidAmount && parseFloat(paidAmount) >= total) 
          ? parseFloat(paidAmount) 
          : null,
        address: deliveryType === 'delivery' ? {
          ...address,
          neighborhood: selectedDeliveryZone?.name || address.neighborhood
        } : null,
        delivery_fee: deliveryFee,
        delivery_zone_name: selectedDeliveryZone?.name || null,
        notes: orderNotes || null,
        items: cart.map(item => ({
          product_id: item.id,
          product_name: item.name,
          quantity: item.quantity,
          price: item.price,
          additionals: (item.additionals || []).map(add => ({
            id: add.id,
            name: add.name,
            additional_name: add.name,
            price: add.price || 0,
            group_name: add.group_name || '',
            group_id: add.group_id || ''
          }))
        })),
        subtotal: subtotal,
        total: total,
        status: 'pendente'
      };

      if (isEditMode && editingOrder?.id) {
        const editPayload = { ...orderData, notify_customer: !!notifyCustomer };
        delete editPayload.order_number;
        delete editPayload.status;
        await axios.patch(`${API}/orders/${editingOrder.id}/edit`, editPayload);
        toast.success('Pedido atualizado! ✏️', {
          description: `Pedido #${editingOrder.order_number} - Novo total: R$ ${total.toFixed(2)}`,
          duration: 4000
        });
      } else {
        await axios.post(`${API}/orders`, orderData);
        toast.success('Pedido criado com sucesso! 🎉', {
          description: `Pedido #${orderNumber} - R$ ${total.toFixed(2)}`,
          duration: 4000
        });
      }

      // Limpar formulário
      clearCart();
      setCustomerName('');
      setCustomerPhone('');
      setTableNumber('');
      setOrderNotes('');
      setPaidAmount('');
      setAddress({
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
        zipcode: ''
      });
      setSelectedDeliveryZone(null);
      setStep('menu');
      
      setTimeout(() => onClose(), 1500);

    } catch (error) {
      console.error('Error creating order:', error);
      toast.error('Erro ao criar pedido', {
        description: error.response?.data?.detail || 'Tente novamente'
      });
    } finally {
      setIsFinalizingOrder(false);
    }
  };

  // Loading state
  if (loading) {
    return ReactDOM.createPortal(
      <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 z-[999999] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-white text-lg font-medium">Carregando PDV...</p>
        </div>
      </div>,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <div
      style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, height: '100vh', overflow: 'hidden', zIndex: 999999, display: 'flex', flexDirection: 'column' }}
    >
      {isEditMode && (
        <div
          data-testid="pdv-edit-banner"
          className="bg-amber-500 text-amber-950 px-3 py-1.5 flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold shadow-md flex-shrink-0"
        >
          <Edit3 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          <span>
            Editando Pedido <span className="font-bold">#{editingOrder?.order_number}</span>
            <span className="hidden sm:inline opacity-70 ml-2">
              (criado em {editingOrder?.created_at ? new Date(editingOrder.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'})
            </span>
          </span>
        </div>
      )}
      <div
        id="pdv-container"
        className="bg-gradient-to-br from-slate-50 via-white to-slate-50 flex flex-col lg:flex-row flex-1 min-h-0"
        style={{ overflow: 'hidden' }}
      >
      {/* ========== HEADER MOBILE/TABLET ========== */}
      <div className="lg:hidden bg-gradient-to-r from-orange-600 via-orange-500 to-orange-600 shadow-2xl flex-shrink-0 z-[100000] sticky top-0">
        <div className="px-3 py-2.5 safe-area-top">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={step === 'checkout' ? () => setStep('menu') : onClose}
                className="w-11 h-11 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-2xl flex items-center justify-center transition-all active:scale-95"
                data-testid="close-pdv-button"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </button>
              <div>
                <h1 className="text-white font-bold text-xl tracking-tight">{step === 'checkout' ? 'Finalizar' : 'PDV'}</h1>
                <p className="text-orange-100 text-xs font-medium">{step === 'checkout' ? 'Pedido' : 'Novo Pedido'}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {step === 'menu' && (
                <>
                  <button
                    onClick={openWeightModal}
                    className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl p-2.5 transition-all active:scale-95"
                    title="Venda por Peso"
                    data-testid="weight-button-mobile"
                  >
                    <Scale className="w-5 h-5 text-white" />
                  </button>
                  <button
                    onClick={() => setShowProductImages(!showProductImages)}
                    className="bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl p-2.5 transition-all active:scale-95"
                    title={showProductImages ? "Ocultar imagens" : "Mostrar imagens"}
                    data-testid="toggle-images-button"
                  >
                    {showProductImages ? (
                      <Eye className="w-5 h-5 text-white" />
                    ) : (
                      <EyeOff className="w-5 h-5 text-white" />
                    )}
                  </button>
                </>
              )}
              
              <button
                onClick={() => setStep(step === 'menu' ? 'checkout' : 'menu')}
                className="relative bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-xl p-2.5 transition-all active:scale-95"
                data-testid="cart-button"
              >
                <ShoppingCart className="w-6 h-6 text-white" />
                {cart.length > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-xs font-bold rounded-full min-w-[22px] h-[22px] flex items-center justify-center animate-pulse px-1.5">
                    {itemCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Mini Cart Summary - Removed to save space */}
        </div>
      </div>

      {/* ========== MAIN CONTENT AREA ========== */}
      <div className="flex-1 flex flex-col lg:flex-row h-full min-h-0 min-w-0 overflow-hidden">
        
        {/* ========== LEFT SIDE - PRODUCTS (Mobile: full, Desktop: 60%) ========== */}
        <PDVProductsPanel
          onClose={onClose}
          openWeightModal={openWeightModal}
          showProductImages={showProductImages}
          setShowProductImages={setShowProductImages}
          step={step}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          products={products}
          categoriesWithCount={categoriesWithCount}
          filteredProducts={filteredProducts}
          addingToCart={addingToCart}
          cart={cart}
          addToCart={addToCart}
        />

        {/* ========== RIGHT SIDE - CART/CHECKOUT (Mobile: full screen, Desktop: 40% sidebar) ========== */}
        <div className={`flex flex-col bg-white h-full min-h-0 lg:basis-[40%] lg:grow-0 lg:shrink-0 lg:min-w-0 lg:max-w-[40%] lg:border-l-2 lg:border-slate-200 ${step === 'menu' ? 'hidden lg:flex' : 'flex flex-1 lg:flex-none'}`}>

          {/* Desktop Checkout Header */}
          <div className="hidden lg:block bg-gradient-to-r from-slate-800 to-slate-900 px-6 py-4 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h2 className="text-white font-bold text-xl flex items-center gap-2">
                <ShoppingCart className="w-6 h-6" />
                Carrinho
              </h2>
              {cart.length > 0 && (
                <span className="bg-orange-500 text-white text-sm font-bold px-3 py-1 rounded-full">
                  {itemCount} {itemCount === 1 ? 'item' : 'itens'}
                </span>
              )}
            </div>
          </div>

          {/* Cart Content - Scrollable */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-3 lg:px-6 lg:py-4 space-y-3 pdv-scroll" style={{ WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', paddingBottom: '140px' }}>
            
            {/* Cart Items */}
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-3 border-2 border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-slate-800 font-bold text-base lg:text-lg flex items-center gap-2">
                  <Receipt className="w-5 h-5 text-orange-500" />
                  Itens ({cart.length})
                </h3>
                {cart.length > 0 && (
                  <button
                    onClick={clearCart}
                    className="text-red-500 hover:text-red-600 text-sm font-medium flex items-center gap-1"
                  >
                    <Trash2 className="w-4 h-4" />
                    Limpar
                  </button>
                )}
              </div>

              {cart.length > 0 ? (
                <div className="space-y-2">
                  {cart.map((item) => (
                    <div key={item.cartItemId || item.id} className="flex items-start gap-3 bg-white rounded-xl p-3 border border-slate-200 hover:border-orange-500 transition-all">
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-800 font-semibold text-sm truncate">{item.name}</p>
                        {/* Mostrar adicionais se houver */}
                        {item.additionals && item.additionals.length > 0 && (
                          <div className="mt-1 space-y-0.5">
                            {item.additionals.map((add, idx) => (
                              <p key={idx} className="text-slate-500 text-xs flex items-center gap-1">
                                <span className="text-orange-400">+</span>
                                {add.name}
                                {add.price > 0 && <span className="text-slate-400">R$ {add.price.toFixed(2)}</span>}
                              </p>
                            ))}
                          </div>
                        )}
                        <p className="text-orange-600 font-bold text-base mt-0.5">
                          R$ {(item.totalPrice || item.price).toFixed(2)}
                        </p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => updateQuantity(item.cartItemId || item.id, item.quantity - 1)}
                          className="w-8 h-8 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg flex items-center justify-center transition-all active:scale-90"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="text-slate-800 font-bold w-10 text-center text-base">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.cartItemId || item.id, item.quantity + 1)}
                          className="w-8 h-8 bg-green-50 hover:bg-green-100 text-green-600 rounded-lg flex items-center justify-center transition-all active:scale-90"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeFromCart(item.cartItemId || item.id)}
                          className="ml-1 text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="lg:hidden text-center py-12">
                  <ShoppingCart className="w-16 h-16 mx-auto mb-3 text-slate-300" />
                  <p className="text-slate-400 font-medium">Carrinho vazio</p>
                  <p className="text-slate-400 text-sm mt-1">Adicione produtos para continuar</p>
                </div>
              )}
            </div>

            {cart.length > 0 ? null : (
              <div className="hidden lg:flex flex-col items-center justify-center py-6 text-center bg-orange-50/50 border border-dashed border-orange-200 rounded-xl">
                <ShoppingCart className="w-10 h-10 mb-2 text-orange-300" />
                <p className="text-slate-500 text-sm font-medium">Adicione produtos ao carrinho</p>
                <p className="text-slate-400 text-xs mt-0.5">Você pode preencher os dados do cliente abaixo enquanto isso</p>
              </div>
            )}

            {/* ========== FORMULÁRIOS ========== */}
            {/* Mobile: aparecem só após adicionar item. Desktop: sempre visíveis */}
            <div className={cart.length === 0 ? 'hidden lg:block lg:opacity-70' : ''}>
              <>
                {/* Customer Data */}
                <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-3 border-2 border-blue-200 mt-3">
                  <h3 className="text-slate-800 font-bold text-base lg:text-lg mb-3 flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-600" />
                    Dados do Cliente
                  </h3>
                  <div className="space-y-3">
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        type="text"
                        placeholder="Nome do cliente *"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        data-testid="customer-name-input"
                      />
                    </div>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        type="tel"
                        placeholder="Telefone (opcional)"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        data-testid="customer-phone-input"
                      />
                    </div>
                  </div>
                </div>

                {/* Delivery Type */}
                <div className="bg-gradient-to-br from-purple-50 to-white rounded-xl p-3 border-2 border-purple-200">
                  <h3 className="text-slate-800 font-bold text-base lg:text-lg mb-3 flex items-center gap-2">
                    <Bike className="w-5 h-5 text-purple-600" />
                    Tipo de Entrega
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => setDeliveryType('delivery')}
                      className={`p-2.5 rounded-xl border-2 transition-all ${
                        deliveryType === 'delivery'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid="delivery-type-delivery"
                    >
                      <Bike className={`w-5 h-5 mx-auto mb-1 ${deliveryType === 'delivery' ? 'text-orange-600' : 'text-slate-400'}`} />
                      <p className={`text-xs lg:text-sm font-bold ${deliveryType === 'delivery' ? 'text-orange-600' : 'text-slate-600'}`}>Delivery</p>
                    </button>
                    <button
                      onClick={() => setDeliveryType('balcao')}
                      className={`p-2.5 rounded-xl border-2 transition-all ${
                        deliveryType === 'balcao'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid="delivery-type-balcao"
                    >
                      <Store className={`w-5 h-5 mx-auto mb-1 ${deliveryType === 'balcao' ? 'text-orange-600' : 'text-slate-400'}`} />
                      <p className={`text-xs lg:text-sm font-bold ${deliveryType === 'balcao' ? 'text-orange-600' : 'text-slate-600'}`}>Balcão</p>
                    </button>
                    <button
                      onClick={() => setDeliveryType('mesa')}
                      className={`p-2.5 rounded-xl border-2 transition-all ${
                        deliveryType === 'mesa'
                          ? 'border-orange-500 bg-orange-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid="delivery-type-mesa"
                    >
                      <Home className={`w-5 h-5 mx-auto mb-1 ${deliveryType === 'mesa' ? 'text-orange-600' : 'text-slate-400'}`} />
                      <p className={`text-xs lg:text-sm font-bold ${deliveryType === 'mesa' ? 'text-orange-600' : 'text-slate-600'}`}>Mesa</p>
                    </button>
                  </div>

                  {deliveryType === 'delivery' && (
                    <button
                      onClick={() => setShowAddressModal(true)}
                      className={`w-full mt-3 px-4 py-3 bg-white hover:bg-slate-50 border-2 rounded-xl text-slate-800 flex items-center justify-between transition-all ${
                        address.street && selectedDeliveryZone ? 'border-green-400' : 'border-slate-200'
                      }`}
                      data-testid="open-address-modal"
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <MapPin className={`w-5 h-5 flex-shrink-0 ${address.street && selectedDeliveryZone ? 'text-green-600' : 'text-orange-600'}`} />
                        <div className="flex flex-col items-start min-w-0">
                          {address.street && selectedDeliveryZone ? (
                            <>
                              <span className="text-sm font-medium truncate max-w-full">
                                {address.street}, {address.number}
                              </span>
                              <span className="text-xs text-green-600 font-semibold">
                                {selectedDeliveryZone.name} • Taxa: R$ {selectedDeliveryZone.fee.toFixed(2)}
                              </span>
                            </>
                          ) : (
                            <span className="text-sm font-medium text-orange-600">
                              📍 Adicionar endereço
                            </span>
                          )}
                        </div>
                      </div>
                      <span className={`font-bold flex-shrink-0 ${address.street && selectedDeliveryZone ? 'text-green-600' : 'text-orange-600'}`}>
                        {address.street && selectedDeliveryZone ? '✓' : '→'}
                      </span>
                    </button>
                  )}

                  {deliveryType === 'mesa' && (
                    <input
                      type="text"
                      placeholder="Número da mesa *"
                      value={tableNumber}
                      onChange={(e) => setTableNumber(e.target.value)}
                      className="w-full mt-3 px-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 text-sm"
                      data-testid="table-number-input"
                    />
                  )}
                </div>

                {/* Payment Method */}
                <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-3 border-2 border-green-200">
                  <h3 className="text-slate-800 font-bold text-base lg:text-lg mb-3 flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-green-600" />
                    Forma de Pagamento
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setPaymentMethod('dinheiro')}
                      className={`p-2.5 rounded-xl border-2 transition-all ${
                        paymentMethod === 'dinheiro'
                          ? 'border-green-500 bg-green-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid="payment-method-dinheiro"
                    >
                      <Wallet className={`w-5 h-5 mx-auto mb-1 ${paymentMethod === 'dinheiro' ? 'text-green-600' : 'text-slate-400'}`} />
                      <p className={`text-xs lg:text-sm font-bold ${paymentMethod === 'dinheiro' ? 'text-green-600' : 'text-slate-600'}`}>Dinheiro</p>
                    </button>
                    <button
                      onClick={() => {
                        setPaymentMethod('cartao');
                        setPaidAmount('');
                      }}
                      className={`p-2.5 rounded-xl border-2 transition-all ${
                        paymentMethod === 'cartao'
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid="payment-method-cartao"
                    >
                      <CreditCard className={`w-5 h-5 mx-auto mb-1 ${paymentMethod === 'cartao' ? 'text-blue-600' : 'text-slate-400'}`} />
                      <p className={`text-xs lg:text-sm font-bold ${paymentMethod === 'cartao' ? 'text-blue-600' : 'text-slate-600'}`}>Cartão</p>
                    </button>
                    <button
                      onClick={() => {
                        setPaymentMethod('pix');
                        setPaidAmount('');
                      }}
                      className={`p-2.5 rounded-xl border-2 transition-all ${
                        paymentMethod === 'pix'
                          ? 'border-cyan-500 bg-cyan-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid="payment-method-pix"
                    >
                      <span className={`text-xl lg:text-2xl font-bold mx-auto mb-1 block ${paymentMethod === 'pix' ? 'text-cyan-600' : 'text-slate-400'}`}>PIX</span>
                      <p className={`text-xs lg:text-sm font-bold ${paymentMethod === 'pix' ? 'text-cyan-600' : 'text-slate-600'}`}>PIX</p>
                    </button>
                    <button
                      onClick={() => {
                        setPaymentMethod('credito');
                        setPaidAmount('');
                      }}
                      className={`p-2.5 rounded-xl border-2 transition-all ${
                        paymentMethod === 'credito'
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-slate-200 bg-white hover:border-slate-300'
                      }`}
                      data-testid="payment-method-credito"
                    >
                      <CreditCard className={`w-5 h-5 mx-auto mb-1 ${paymentMethod === 'credito' ? 'text-purple-600' : 'text-slate-400'}`} />
                      <p className={`text-xs lg:text-sm font-bold ${paymentMethod === 'credito' ? 'text-purple-600' : 'text-slate-600'}`}>Crédito</p>
                    </button>
                  </div>

                  {/* Campo de Troco - Aparece quando Dinheiro é selecionado */}
                  {paymentMethod === 'dinheiro' && (
                    <div className="mt-3 space-y-2 animate-in slide-in-from-top">
                      <div className="relative">
                        <label className="text-slate-700 text-sm font-semibold mb-1 block">
                          Valor Pago pelo Cliente
                        </label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 font-bold">R$</span>
                          <input
                            type="number"
                            step="0.01"
                            min={total}
                            placeholder="0,00"
                            value={paidAmount}
                            onChange={(e) => setPaidAmount(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 bg-white border-2 border-green-300 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 text-base font-semibold"
                            data-testid="paid-amount-input"
                          />
                        </div>
                      </div>

                      {/* Mostrar Troco */}
                      {paidAmount && parseFloat(paidAmount) >= total && (
                        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-3 shadow-lg animate-in scale-in">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <div className="bg-white/20 rounded-lg p-2">
                                <Wallet className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <p className="text-white text-xs font-medium">Troco</p>
                                <p className="text-white font-bold text-2xl">R$ {changeAmount.toFixed(2)}</p>
                              </div>
                            </div>
                            <CheckCircle className="w-8 h-8 text-white" />
                          </div>
                        </div>
                      )}

                      {/* Aviso se valor for menor */}
                      {paidAmount && parseFloat(paidAmount) < total && (
                        <div className="bg-red-50 border-2 border-red-300 rounded-xl p-2.5 flex items-center gap-2">
                          <span className="text-red-600 text-sm font-semibold">
                            ⚠️ Valor pago é menor que o total do pedido
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Order Notes */}
                <div className="bg-gradient-to-br from-amber-50 to-white rounded-xl p-3 border-2 border-amber-200">
                  <h3 className="text-slate-800 font-bold text-base mb-2 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-amber-600" />
                    Observações (Opcional)
                  </h3>
                  <textarea
                    placeholder="Ex: Sem cebola, caprichar no molho..."
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm resize-none"
                    data-testid="order-notes-input"
                  />
                </div>
              </>
            </div>
          </div>

          {/* Total and Finalize - Fixed Bottom (desktop: always visible; mobile: only when cart has items) */}
          <div className={`${cart.length === 0 ? 'hidden lg:flex' : 'flex'} fixed bottom-0 left-0 right-0 lg:relative lg:bottom-auto flex-shrink-0 bg-gradient-to-br from-slate-800 to-slate-900 px-3 py-3 lg:px-6 lg:py-4 border-t-4 border-orange-500 shadow-2xl z-[100001] safe-area-bottom flex-col`}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-slate-300 text-xs font-medium">Total do Pedido</p>
                <p className="text-white font-bold text-2xl lg:text-3xl">R$ {total.toFixed(2)}</p>
                <div className="flex flex-col text-xs">
                  <span className="text-slate-400">{itemCount} {itemCount === 1 ? 'item' : 'itens'} • Subtotal: R$ {subtotal.toFixed(2)}</span>
                  {deliveryType === 'delivery' && deliveryFee > 0 && (
                    <span className="text-orange-400">+ Taxa de entrega: R$ {deliveryFee.toFixed(2)}</span>
                  )}
                </div>
              </div>
            </div>
            {isEditMode && (
              <label
                className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2.5 mb-3 cursor-pointer hover:bg-amber-500/15 transition-colors"
                data-testid="pdv-notify-customer-toggle"
              >
                <input
                  type="checkbox"
                  checked={notifyCustomer}
                  onChange={(e) => setNotifyCustomer(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-amber-500 cursor-pointer shrink-0"
                />
                <div className="flex-1 text-xs leading-snug">
                  <p className="font-semibold text-amber-200">📲 Notificar cliente via WhatsApp</p>
                  <p className="text-amber-300/70 mt-0.5">Envia um resumo das alterações para {editingOrder?.customer_phone || 'o número do pedido'}.</p>
                </div>
              </label>
            )}
            <button
              onClick={finalizarPedido}
              disabled={isFinalizingOrder || cart.length === 0}
              className={`w-full py-3.5 ${isEditMode ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700' : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700'} text-white font-bold rounded-xl shadow-2xl transition-all flex items-center justify-center gap-2 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed text-base`}
              data-testid="finalize-order-button"
            >
              {isFinalizingOrder ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {isEditMode ? 'Salvando...' : 'Finalizando...'}
                </>
              ) : (
                <>
                  {isEditMode ? <Edit3 className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
                  {cart.length === 0
                    ? (isEditMode ? 'Adicione itens para salvar' : 'Adicione itens para finalizar')
                    : (isEditMode ? `Salvar alterações no pedido #${editingOrder?.order_number}` : 'Finalizar Pedido')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ========== ADDRESS MODAL - NOVO DESIGN ========== */}
      {showAddressModal && (
        <PDVAddressModal
          setShowAddressModal={setShowAddressModal}
          loadingZones={loadingZones}
          deliveryZones={deliveryZones}
          selectedDeliveryZone={selectedDeliveryZone}
          setSelectedDeliveryZone={setSelectedDeliveryZone}
          address={address}
          setAddress={setAddress}
        />
      )}

      {/* ========== MODAL DE VENDA POR PESO ========== */}
      {showWeightModal && (
        <PDVWeightModal
          setShowWeightModal={setShowWeightModal}
          loadingWeightItems={loadingWeightItems}
          showNewWeightItemForm={showNewWeightItemForm}
          setShowNewWeightItemForm={setShowNewWeightItemForm}
          setEditingWeightItem={setEditingWeightItem}
          editingWeightItem={editingWeightItem}
          newWeightItemName={newWeightItemName}
          setNewWeightItemName={setNewWeightItemName}
          newWeightItemPrice={newWeightItemPrice}
          setNewWeightItemPrice={setNewWeightItemPrice}
          updateWeightItem={updateWeightItem}
          createWeightItem={createWeightItem}
          weightItems={weightItems}
          selectedWeightItem={selectedWeightItem}
          setSelectedWeightItem={setSelectedWeightItem}
          weightInGrams={weightInGrams}
          setWeightInGrams={setWeightInGrams}
          startEditWeightItem={startEditWeightItem}
          deleteWeightItem={deleteWeightItem}
          calculateWeightTotal={calculateWeightTotal}
          addWeightItemToCart={addWeightItemToCart}
        />
      )}

      {/* ========== MODAL DE ADICIONAIS ========== */}
      {showAdditionalsModal && selectedProduct && (
        <PDVAdditionalsModal
          selectedProduct={selectedProduct}
          setShowAdditionalsModal={setShowAdditionalsModal}
          setSelectedProduct={setSelectedProduct}
          setSelectedAdditionals={setSelectedAdditionals}
          setProductQuantity={setProductQuantity}
          setProductGroupsWithItems={setProductGroupsWithItems}
          loadingAdditionals={loadingAdditionals}
          productGroupsWithItems={productGroupsWithItems}
          selectedAdditionals={selectedAdditionals}
          toggleAdditional={toggleAdditional}
          productQuantity={productQuantity}
          addToCartWithAdditionals={addToCartWithAdditionals}
          canAddToCart={canAddToCart}
          calculateProductTotal={calculateProductTotal}
        />
      )}
      </div>
    </div>,
    document.body
  );
};

export default PDVScreen;
